import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useData } from '@happyspace/sdk/data';

export default function ReservationsPage() {
  const { insert, loading, error } = useData('reservations');
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', date: '', time: '', guests: '2', requests: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await insert({ ...formData, submittedAt: new Date().toISOString() });
      setSubmitted(true);
    } catch (err) {
      console.error('Reservation failed:', err);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] pt-20 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-12 rounded-3xl shadow-xl text-center max-w-lg w-full border border-gray-100"
        >
          <div className="w-24 h-24 bg-[#0054e9] rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Table Reserved!</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Thank you, <strong>{formData.name}</strong>. Your table for {formData.guests} guests
            on {formData.date} at {formData.time} has been requested. We'll confirm shortly.
          </p>
          <Link
            to="/"
            className="inline-block bg-[#0054e9] text-white px-10 py-4 rounded-full font-bold hover:bg-[#015796] transition-colors"
          >
            Back to Home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0] pt-20 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-10 font-semibold group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" /> Back to Home
        </Link>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
          <div className="grid md:grid-cols-5">
            {/* Left sidebar */}
            <div className="md:col-span-2 bg-[#0A0F1E] p-10 text-white flex flex-col justify-between">
              <div>
                <span className="text-[#D4AF37] font-bold tracking-widest uppercase text-xs mb-4 block">Reservations</span>
                <h1 className="text-3xl font-bold mb-5">Secure Your Table</h1>
                <p className="text-gray-400 leading-relaxed text-sm">
                  Join us for an authentic Indian dining experience. For parties larger than 8, please contact us directly.
                </p>
              </div>
              <div className="space-y-5 mt-10">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#0054e9]/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#0054e9]" />
                  </div>
                  <span className="text-sm font-medium text-gray-300">Mon – Sun, 11AM – 10PM</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#0054e9]/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#0054e9]" />
                  </div>
                  <span className="text-sm font-medium text-gray-300">Private Dining Available</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <span className="text-sm font-medium text-gray-300">Catering & Events</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-3 p-10">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Full Name</label>
                    <input required className="w-full p-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9]" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Phone</label>
                    <input required type="tel" className="w-full p-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9]" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Email</label>
                  <input required type="email" className="w-full p-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9]" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input required type="date" className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9]" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Time</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input required type="time" className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9]" value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Guests</label>
                  <select className="w-full p-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9]" value={formData.guests} onChange={e => setFormData({ ...formData, guests: e.target.value })}>
                    {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Special Requests</label>
                  <textarea className="w-full p-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-[#0054e9] resize-none" rows={3} placeholder="Allergies, anniversaries, seating preferences..." value={formData.requests} onChange={e => setFormData({ ...formData, requests: e.target.value })} />
                </div>

                <button
                  disabled={loading}
                  type="submit"
                  className="w-full bg-[#0054e9] text-white py-4 rounded-full font-bold text-lg hover:bg-[#015796] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm Reservation'}
                </button>

                {error && <p className="text-red-500 text-sm text-center">{error.message}</p>}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
